/**
 * The text of a support message, with its pictures standing in it.
 *
 * The body is plain text where a line reading `[picture N]` places picture N
 * (`support-picture-tokens`). A textarea showed exactly that — the lines, with
 * a numbered strip of thumbnails above to decode them by — so what you wrote
 * looked nothing like what was sent. Here each such line IS the picture, and
 * the box reads text, picture, text, the way the sent message does.
 *
 * Lexical in plain-text mode, not a hand-rolled contentEditable: caret
 * movement, deletion, IME, paste and undo around a non-editable block are what
 * a hand-rolled one gets wrong in each browser differently. Plain text only —
 * a paste is its text, Enter is a newline, there is no formatting to apply.
 * One paragraph holds the whole body: newlines are its line breaks and a
 * picture is an inline node on a line of its own, so the editor's text is the
 * body character for character.
 */

import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { PlainTextPlugin } from "@lexical/react/LexicalPlainTextPlugin";
import Box from "@mui/material/Box";
import {
	$createLineBreakNode,
	$createParagraphNode,
	$createTextNode,
	$getRoot,
	$getSelection,
	$isElementNode,
	$isLineBreakNode,
	$isRangeSelection,
	$isRootNode,
	$isTextNode,
	$setSelection,
	CLEAR_HISTORY_COMMAND,
	COMMAND_PRIORITY_LOW,
	DELETE_CHARACTER_COMMAND,
	type ElementNode,
	HISTORY_MERGE_TAG,
	type LexicalNode,
	mergeRegister,
	PASTE_COMMAND,
	type PointType,
	SKIP_DOM_SELECTION_TAG,
} from "lexical";
import { type Ref, useEffect, useImperativeHandle, useRef } from "react";
import { imageFromPaste } from "./support-capture-engines";
import {
	$createSupportPictureNode,
	$isSupportPictureNode,
	$keepPictureOnItsLine,
	type ComposerPictures,
	ComposerPicturesContext,
	PICTURE_CLASS,
	SupportPictureNode,
} from "./support-composer-picture-node";
import type { DraftPicture } from "./support-draft-pictures";
import { pictureNumber } from "./support-picture-tokens";

/** Where the caret is, for a caller that writes at it. */
export interface ComposerCaret {
	/** Offset into the body, or null when the box has never had a caret. */
	offset: () => number | null;
}

/** Marks the editor rewriting itself from `value`, which is not an edit to report. */
const SYNC_TAG = "support-composer-sync";

const LINE_HEIGHT_EM = 1.4375;

const $setBody = (body: string): void => {
	const paragraph = $createParagraphNode();
	body.split("\n").forEach((line, index) => {
		if (index > 0) paragraph.append($createLineBreakNode());
		if (pictureNumber(line) !== null)
			paragraph.append($createSupportPictureNode(line));
		else if (line) paragraph.append($createTextNode(line));
	});
	$getRoot().clear().append(paragraph);
};

const $paragraphs = (): ElementNode[] =>
	$getRoot().getChildren().filter($isElementNode);

const $body = (): string =>
	$paragraphs()
		.map((paragraph) => paragraph.getTextContent())
		.join("\n");

const $pictureNumbers = (): number[] =>
	$paragraphs()
		.flatMap((paragraph) => paragraph.getChildren())
		.filter($isSupportPictureNode)
		.map((node) => node.getNumber());

const sizeOf = (nodes: LexicalNode[]): number =>
	nodes.reduce((sum, node) => sum + node.getTextContentSize(), 0);

/** Body offset of a selection point. */
const $pointOffset = (point: PointType): number => {
	const node = point.getNode();
	const paragraphs = $paragraphs();
	const before = (index: number): number =>
		paragraphs
			.slice(0, index)
			.reduce((sum, p) => sum + p.getTextContentSize() + 1, 0);
	if ($isRootNode(node)) return before(point.offset);
	if (point.type === "element" && $isElementNode(node)) {
		return (
			before(node.getIndexWithinParent()) +
			sizeOf(node.getChildren().slice(0, point.offset))
		);
	}
	return (
		before(node.getParentOrThrow().getIndexWithinParent()) +
		sizeOf(node.getPreviousSiblings()) +
		point.offset
	);
};

const $caretOffset = (): number | null => {
	const selection = $getSelection();
	if (!$isRangeSelection(selection)) return null;
	return $pointOffset(selection.isBackward() ? selection.focus : selection.anchor);
};

/** Puts the caret at a body offset; one inside a picture's line lands after it. */
const $selectOffset = (offset: number): void => {
	let start = 0;
	for (const paragraph of $paragraphs()) {
		const size = paragraph.getTextContentSize();
		if (offset <= start + size) {
			let position = start;
			const children = paragraph.getChildren();
			for (const [index, child] of children.entries()) {
				if (offset <= position) {
					paragraph.select(index, index);
					return;
				}
				const childSize = child.getTextContentSize();
				if ($isTextNode(child) && offset <= position + childSize) {
					child.select(offset - position, offset - position);
					return;
				}
				position += childSize;
			}
			paragraph.select(children.length, children.length);
			return;
		}
		start += size + 1;
	}
	$getRoot().selectEnd();
};

/**
 * Where a caret goes when the body changes under it: it keeps its place in
 * the text before the change, moves with the text after it, and lands after
 * anything inserted at it — a picture added at the caret leaves you typing
 * below the picture.
 */
const mapCaret = (caret: number, before: string, after: string): number => {
	let prefix = 0;
	while (
		prefix < before.length &&
		prefix < after.length &&
		before[prefix] === after[prefix]
	) {
		prefix++;
	}
	let suffix = 0;
	while (
		suffix < before.length - prefix &&
		suffix < after.length - prefix &&
		before[before.length - 1 - suffix] === after[after.length - 1 - suffix]
	) {
		suffix++;
	}
	if (caret >= before.length - suffix) return caret + after.length - before.length;
	if (caret <= prefix) return caret;
	return after.length - suffix;
};

/**
 * Backspace at the start of the line under a picture, or Delete at the end of
 * the line above one, means that picture. Left alone it would delete only the
 * newline between them, which the picture's own-line rule puts straight back.
 */
const $deletePictureAcrossBreak = (isBackward: boolean): boolean => {
	const selection = $getSelection();
	if (!$isRangeSelection(selection) || !selection.isCollapsed()) return false;
	const { anchor } = selection;
	const node = anchor.getNode();
	let before: LexicalNode | null;
	let after: LexicalNode | null;
	if (anchor.type === "text") {
		if (anchor.offset === 0) {
			before = node.getPreviousSibling();
			after = node;
		} else if (anchor.offset === node.getTextContentSize()) {
			before = node;
			after = node.getNextSibling();
		} else {
			return false;
		}
	} else if ($isElementNode(node) && !$isRootNode(node)) {
		before = node.getChildAtIndex(anchor.offset - 1);
		after = node.getChildAtIndex(anchor.offset);
	} else {
		return false;
	}
	const lineBreak = isBackward ? before : after;
	const neighbour = isBackward ? after : before;
	if (!$isLineBreakNode(lineBreak) || !neighbour || $isLineBreakNode(neighbour))
		return false;
	const picture = isBackward
		? lineBreak.getPreviousSibling()
		: lineBreak.getNextSibling();
	if (!$isSupportPictureNode(picture)) return false;
	picture.remove();
	lineBreak.remove();
	return true;
};

interface SyncProps {
	value: string;
	onChange: (value: string) => void;
	pictures: DraftPicture[];
	onAddPicture: (blob: Blob) => void;
	onRemovePicture: (key: number) => void;
	caretRef?: Ref<ComposerCaret>;
}

/** Keeps the editor and `value` the same text, in both directions. */
const BodySyncPlugin: React.FC<SyncProps> = ({
	value,
	onChange,
	pictures,
	onAddPicture,
	onRemovePicture,
	caretRef,
}) => {
	const [editor] = useLexicalComposerContext();
	// The body the editor holds, as last reported or applied.
	const shown = useRef(value);
	// Registered once; the callbacks change every render.
	const latest = useRef({ onChange, pictures, onAddPicture, onRemovePicture });
	latest.current = { onChange, pictures, onAddPicture, onRemovePicture };

	useImperativeHandle(
		caretRef,
		() => ({ offset: () => editor.getEditorState().read($caretOffset) }),
		[editor]
	);

	useEffect(
		() =>
			mergeRegister(
				editor.registerNodeTransform(SupportPictureNode, $keepPictureOnItsLine),
				editor.registerCommand(
					DELETE_CHARACTER_COMMAND,
					$deletePictureAcrossBreak,
					COMMAND_PRIORITY_LOW
				),
				editor.registerCommand(
					PASTE_COMMAND,
					(event) => {
						// Ctrl+V after a snipping tool, which is how most people
						// take a screenshot at all. Taken over ONLY when the
						// clipboard really holds an image — a text paste stays
						// the plain-text paste underneath.
						const pasted =
							event instanceof ClipboardEvent
								? imageFromPaste(event.clipboardData)
								: null;
						if (!pasted) return false;
						event.preventDefault();
						latest.current.onAddPicture(pasted);
						return true;
					},
					COMMAND_PRIORITY_LOW
				),
				editor.registerUpdateListener(
					({
						editorState,
						prevEditorState,
						tags,
						dirtyElements,
						dirtyLeaves,
					}) => {
						if (
							tags.has(SYNC_TAG) ||
							(dirtyElements.size === 0 && dirtyLeaves.size === 0)
						) {
							return;
						}
						const body = editorState.read($body);
						const kept = editorState.read($pictureNumbers);
						const gone = prevEditorState
							.read($pictureNumbers)
							.filter((number) => !kept.includes(number));
						if (body !== shown.current) {
							shown.current = body;
							latest.current.onChange(body);
						}
						// Highest first: each removal renumbers only the pictures
						// after it, so the lower numbers still name theirs.
						const { pictures: current, onRemovePicture: remove } =
							latest.current;
						for (const number of gone.sort((a, b) => b - a)) {
							const picture = current[number - 1];
							// A line whose picture was still being fetched has none.
							if (picture) remove(picture.key);
						}
						// Undo cannot bring back a picture that has been let go.
						if (gone.length) {
							queueMicrotask(() =>
								editor.dispatchCommand(CLEAR_HISTORY_COMMAND, undefined)
							);
						}
					}
				)
			),
		[editor]
	);

	// A body set from outside: a draft inserted, the box emptied after
	// sending, a picture's line added or renumbered.
	useEffect(() => {
		if (value === shown.current) return;
		const before = shown.current;
		shown.current = value;
		const focused = editor.getRootElement() === document.activeElement;
		editor.update(
			() => {
				const caret = $caretOffset();
				$setBody(value);
				if (caret === null) $setSelection(null);
				else $selectOffset(mapCaret(caret, before, value));
			},
			{
				// Not focused: keep the caret for the next picture without
				// pulling focus back from the dialog that took it.
				tag: focused
					? [SYNC_TAG, HISTORY_MERGE_TAG]
					: [SYNC_TAG, HISTORY_MERGE_TAG, SKIP_DOM_SELECTION_TAG],
				// Undo cannot step back into a body that is no longer this one.
				onUpdate: () =>
					editor.dispatchCommand(CLEAR_HISTORY_COMMAND, undefined),
			}
		);
	}, [editor, value]);

	return null;
};

interface SupportComposerEditorProps extends SyncProps {
	placeholder: string;
	onEditPicture: (key: number) => void;
	compact: boolean;
	minRows: number;
	maxRows?: number;
	autoFocus: boolean;
	/** Padding around the text, in theme spacing. */
	px: number;
	py: number;
	dataComponent: string;
}

const SupportComposerEditor: React.FC<SupportComposerEditorProps> = ({
	placeholder,
	onEditPicture,
	compact,
	minRows,
	maxRows,
	autoFocus,
	px,
	py,
	dataComponent,
	...sync
}) => {
	const pictures: ComposerPictures = {
		pictures: sync.pictures,
		onEdit: onEditPicture,
		onRemove: sync.onRemovePicture,
		compact,
	};

	return (
		<LexicalComposer
			initialConfig={{
				namespace: "support-composer",
				nodes: [SupportPictureNode],
				editorState: () => $setBody(sync.value),
				onError: (error) => {
					throw error;
				},
			}}
		>
			<Box
				sx={(theme) => ({
					position: "relative",
					flex: 1,
					minWidth: 0,
					...theme.typography.body1,
					fontSize: compact ? 14 : undefined,
					lineHeight: `${LINE_HEIGHT_EM}em`,
					"& > [contenteditable]": {
						px,
						py,
						outline: "none",
						whiteSpace: "pre-wrap",
						overflowWrap: "anywhere",
						minHeight: `calc(${minRows * LINE_HEIGHT_EM}em + ${theme.spacing(py * 2)})`,
						maxHeight: maxRows
							? `calc(${maxRows * LINE_HEIGHT_EM}em + ${theme.spacing(py * 2)})`
							: undefined,
						overflowY: maxRows ? "auto" : undefined,
					},
					"& p": { m: 0 },
					// A picture is the whole of its line, sitting on it like a
					// tall letter, with a little air above and below.
					[`& .${PICTURE_CLASS}`]: {
						display: "inline-block",
						verticalAlign: "bottom",
						my: 0.5,
					},
				})}
				data-component={`${dataComponent}-editor`}
			>
				<ComposerPicturesContext.Provider value={pictures}>
					<PlainTextPlugin
						contentEditable={
							<ContentEditable
								aria-placeholder={placeholder}
								placeholder={
									<Box
										sx={{
											position: "absolute",
											top: 0,
											left: 0,
											px,
											py,
											color: "text.disabled",
											pointerEvents: "none",
											userSelect: "none",
										}}
									>
										{placeholder}
									</Box>
								}
								data-component={`${dataComponent}-input`}
							/>
						}
						ErrorBoundary={LexicalErrorBoundary}
					/>
				</ComposerPicturesContext.Provider>
			</Box>
			<HistoryPlugin />
			{autoFocus && <AutoFocusPlugin />}
			<BodySyncPlugin {...sync} />
		</LexicalComposer>
	);
};

export default SupportComposerEditor;
